import { useMemo } from "react";
import { pluralizeWithCount } from "@common/helpers/string";
import { Picker } from "@design-system/Picker/Picker.lib";
import type {
    PickerItemData,
    PickerTriggerButtonProps,
} from "@design-system/Picker/Picker.types";
import { Tooltip } from "@design-system/Tooltip/Tooltip";

interface MultiSelectFilterComboboxProps<T extends string> {
    items: T[];
    selectedValues: T[];
    onValuesChange: (values: T[]) => void;
    getLabel: (item: T) => string;
    placeholder: string;
    emptyMessage: string;
    /** Renders a search bar in the popup when provided. */
    searchPlaceholder?: string;
    /** When provided, the trigger shows "N <noun>" (correctly pluralized) instead of "N selected". */
    selectedNoun?: string;
    showClearButton?: boolean;
    triggerButtonProps?: Pick<
        PickerTriggerButtonProps,
        "className" | "block" | "disabled"
    >;
}

export const MultiSelectFilterCombobox = <T extends string>({
    items,
    selectedValues,
    onValuesChange,
    getLabel,
    placeholder,
    emptyMessage,
    searchPlaceholder,
    selectedNoun,
    showClearButton = true,
    triggerButtonProps,
}: MultiSelectFilterComboboxProps<T>) => {
    const pickerItems = useMemo<Array<PickerItemData<T>>>(
        () => items.map((item) => ({ value: item, label: getLabel(item) })),
        [getLabel, items],
    );

    // A single selection is unambiguous, so it shows its own label; 2+
    // collapse to a count with the selected labels in a tooltip.
    const renderTriggerValue = (values: readonly T[]) => {
        if (values.length === 1) {
            return getLabel(values[0]);
        }
        const tooltip = values.map(getLabel).join(", ");
        // Remount when the content changes: the lazy tooltip can stop
        // reopening if its content shifts while it stays mounted.
        return (
            <Tooltip key={tooltip} content={tooltip}>
                <span>
                    {selectedNoun
                        ? pluralizeWithCount(selectedNoun, values.length)
                        : `${values.length} selected`}
                </span>
            </Tooltip>
        );
    };

    return (
        <Picker.Select
            multiple
            items={pickerItems}
            value={selectedValues}
            onValueChange={onValuesChange}
            placeholder={placeholder}
            searchable={searchPlaceholder !== undefined}
            searchPlaceholder={searchPlaceholder}
            emptyLabel={emptyMessage}
            renderValue={renderTriggerValue}
            renderFooter={
                showClearButton && selectedValues.length > 0
                    ? () => (
                          <Picker.Item
                              closeOnSelect={false}
                              onSelect={() => onValuesChange([])}
                          >
                              Show all
                          </Picker.Item>
                      )
                    : undefined
            }
            trigger={
                <Picker.Button
                    variant="primary"
                    size="md"
                    {...triggerButtonProps}
                />
            }
        />
    );
};
